import type { CSSProperties } from "react";

export type ComputeHeaderSurface = "standard" | "dgx" | "none";

export const DGX_RACK_BACKGROUND_FILTER = "brightness(1.0)";
export const DGX_RACK_TEXTURE_BACKGROUND_FILTER = DGX_RACK_BACKGROUND_FILTER;

export const rackSurfaceStyle: CSSProperties = {
  backgroundImage: `
    repeating-linear-gradient(0deg, #161616 0px, #161616 1px, #242424 1px, #242424 2px),
    repeating-linear-gradient(90deg, #161616 0px, #161616 1px, #242424 1px, #242424 2px)
  `,
  boxShadow: `
    0 18px 36px rgba(0, 0, 0, 0.45),
    inset 0 0 18px rgba(0, 0, 0, 0.22)
  `,
};

export const computeRackHoneycombPatternStyle: CSSProperties = {
  backgroundColor: "rgb(28, 28, 28)",
  backgroundImage: `
    linear-gradient(rgba(28, 28, 28, 0.9), rgba(28, 28, 28, 0.9)),
    url("/assets/environment-rack-graphite-honeycomb.jpg")
  `,
  backgroundPosition: "center, center",
  backgroundRepeat: "repeat, repeat",
  backgroundSize: "auto, 500px",
  boxShadow: "inset 0 2px 4px rgba(0, 0, 0, 0.1)",
};

const computeRackTriangleLineColor =
  "color-mix(in srgb, var(--compute-rack-triangle-color) 28%, var(--compute-rack-triangle-background-color))";
const computeRackTriangleLeadingEdge =
  "var(--compute-rack-triangle-leading-edge)";
const computeRackTriangleTrailingEdge =
  "var(--compute-rack-triangle-trailing-edge)";
const computeRackTrianglePeriod = "var(--compute-rack-triangle-period)";
const computeRackTriangleHorizontalOffset =
  "var(--compute-rack-triangle-horizontal-offset)";

type ComputeRackTrianglePatternStyle = CSSProperties &
  Record<
    | "--compute-rack-triangle-color"
    | "--compute-rack-triangle-background-color"
    | "--compute-rack-triangle-leading-edge"
    | "--compute-rack-triangle-trailing-edge"
    | "--compute-rack-triangle-period"
    | "--compute-rack-triangle-horizontal-offset",
    string
  >;

export const computeRackGoldTrianglePatternStyle: ComputeRackTrianglePatternStyle =
  {
    "--compute-rack-triangle-color": "rgb(66 63 60 / 40%)",
    "--compute-rack-triangle-background-color": "transparent",
    "--compute-rack-triangle-leading-edge": "1.5px",
    "--compute-rack-triangle-trailing-edge": "9.75px",
    "--compute-rack-triangle-period": "8.75px",
    "--compute-rack-triangle-horizontal-offset": "4.5px",
    backgroundColor: "var(--compute-rack-triangle-background-color)",
    backgroundImage: `
    repeating-linear-gradient(
      60deg,
      ${computeRackTriangleLineColor} 0,
      ${computeRackTriangleLineColor} ${computeRackTriangleLeadingEdge},
      transparent ${computeRackTriangleLeadingEdge},
      transparent ${computeRackTriangleTrailingEdge},
      ${computeRackTriangleLineColor} ${computeRackTriangleTrailingEdge},
      ${computeRackTriangleLineColor} ${computeRackTrianglePeriod}
    ),
    repeating-linear-gradient(
      120deg,
      ${computeRackTriangleLineColor} 0,
      ${computeRackTriangleLineColor} ${computeRackTriangleLeadingEdge},
      transparent ${computeRackTriangleLeadingEdge},
      transparent ${computeRackTriangleTrailingEdge},
      ${computeRackTriangleLineColor} ${computeRackTriangleTrailingEdge},
      ${computeRackTriangleLineColor} ${computeRackTrianglePeriod}
    ),
    repeating-linear-gradient(
      180deg,
      ${computeRackTriangleLineColor} ${computeRackTriangleHorizontalOffset},
      ${computeRackTriangleLineColor} calc(${computeRackTriangleHorizontalOffset} + ${computeRackTriangleLeadingEdge}),
      transparent calc(${computeRackTriangleHorizontalOffset} + ${computeRackTriangleLeadingEdge}),
      transparent calc(${computeRackTriangleHorizontalOffset} + ${computeRackTriangleTrailingEdge}),
      ${computeRackTriangleLineColor} calc(${computeRackTriangleHorizontalOffset} + ${computeRackTriangleTrailingEdge}),
      ${computeRackTriangleLineColor} calc(${computeRackTriangleHorizontalOffset} + ${computeRackTrianglePeriod})
    )
  `,
    backgroundPosition: "0 0",
    backgroundRepeat: "repeat",
    boxShadow: "inset 0 0 16px rgba(0, 0, 0, 0.22)",
  };

export const getComputeHeaderSurfaceStyle = (
  surface: ComputeHeaderSurface,
): CSSProperties | undefined =>
  surface === "dgx"
    ? {}
    : surface === "standard"
      ? computeRackHoneycombPatternStyle
      : undefined;

export const getComputeHeaderSurfaceFilter = (
  surface: ComputeHeaderSurface,
): CSSProperties["filter"] | undefined =>
  surface === "dgx" ? DGX_RACK_TEXTURE_BACKGROUND_FILTER : undefined;
